import groupBy from "lodash/groupBy";

/** @param {unknown} value */
function isPlainObject(value) {
  return value != null && typeof value === "object";
}

/** Merge exercises into a map by their _id.
 * @param {Object[]} exercises
 * @param {Map<string, Object>} byId
 */
function mergeExercisesIntoMap(exercises, byId) {
  for (const ex of exercises) {
    if (ex?._id != null) {
      byId.set(String(ex._id), ex);
    }
  }
}

/** Merge week bucket into a map by their _id.
 * @param {Object} weekBucket
 * @param {Map<string, Object>} byId
 */
function mergeWeekBucketIntoMap(weekBucket, byId) {
  if (!isPlainObject(weekBucket)) {
    return;
  }
  for (const workoutList of Object.values(weekBucket)) {
    if (Array.isArray(workoutList)) {
      mergeExercisesIntoMap(workoutList, byId);
    }
  }
}

/**
 * Flatten cycle-exercises payload `{ [week]: { [workout]: Exercise[] } }` into a
 * deduped list for Program Insights resolution (session ids → exercise docs).
 * @param {Object} [nestedByWeek]
 * @returns {Object[]}
 */
export function flattenCycleExercisesForInsights(nestedByWeek) {
  if (!isPlainObject(nestedByWeek)) {
    return [];
  }

  const byId = new Map();

  for (const weekBucket of Object.values(nestedByWeek)) {
    mergeWeekBucketIntoMap(weekBucket, byId);
  }

  return Array.from(byId.values());
}

/**
 * Program Preview → Program Insights inputs (`builderWeeks` + flat exercises).
 * Prefer `editExercises` (same as Program Editor — includes muscle metadata); fall back to
 * flattened `cycleExercises` when the flat list is unavailable.
 * @param {{ sessions?: Object[] }} [program]
 * @param {Object[]|{ exercises?: Object }} [exercisesSource]
 * @returns {{ builderWeeks: Object, programExercises: Object[] }|null}
 */
export function buildProgramPreviewInsightsInput(program, exercisesSource) {
  const sessions = program?.sessions;
  if (!Array.isArray(sessions) || sessions.length === 0) {
    return null;
  }

  const programExercises = Array.isArray(exercisesSource)
    ? exercisesSource
    : flattenCycleExercisesForInsights(exercisesSource?.exercises);

  return {
    builderWeeks: groupBy(sessions, "week"),
    programExercises,
  };
}
